/**
 * bdr-channel-store.ts
 *
 * Per-channel BDR activity counters for the today window. Backs the
 * QuickLogBar one-tap buttons on the BDR landing page and the stacked-bar
 * channel-split chart.
 *
 * This is the rep-local tally surface. Each tap also fires
 * `logActivity()` to insert an `activity_log` row in Supabase (when the
 * migration is applied — see `dial-log-client.ts`). LocalStorage here
 * is the source of truth for the dashboard tile until the Supabase
 * read path is wired in Option B Week 1 of the activity-audit spec.
 *
 * Storage key: `aip:bdr-channel:{user_id}:{YYYY-MM-DD}` → ChannelCounts
 */

export type BdrChannel =
  | "phone_dial"
  | "phone_connect"
  | "email_send"
  | "email_reply"
  | "linkedin_msg"
  | "linkedin_connect"
  | "sms"
  | "in_person"
  | "meeting_set";

export type ChannelCounts = Record<BdrChannel, number>;

export const CHANNEL_KEYS: BdrChannel[] = [
  "phone_dial",
  "phone_connect",
  "email_send",
  "email_reply",
  "linkedin_msg",
  "linkedin_connect",
  "sms",
  "in_person",
  "meeting_set",
];

export const CHANNEL_LABELS: Record<BdrChannel, string> = {
  phone_dial: "Phone Dial",
  phone_connect: "Phone Connect",
  email_send: "Email Send",
  email_reply: "Email Reply",
  linkedin_msg: "LinkedIn Msg",
  linkedin_connect: "LinkedIn Accept",
  sms: "SMS",
  in_person: "In-Person",
  meeting_set: "Meeting Set",
};

export const CHANNEL_SHORT: Record<BdrChannel, string> = {
  phone_dial: "Dial",
  phone_connect: "Conn",
  email_send: "Email",
  email_reply: "Reply",
  linkedin_msg: "LI Msg",
  linkedin_connect: "LI Acc",
  sms: "SMS",
  in_person: "IRL",
  meeting_set: "Mtg",
};

// Mi6 brand-locked palette: black, charcoal, red, soft accents. No blue.
export const CHANNEL_COLOR: Record<BdrChannel, string> = {
  phone_dial: "#0B0B0F",
  phone_connect: "#16A34A",
  email_send: "#1F1F23",
  email_reply: "#16A34A",
  linkedin_msg: "#525258",
  linkedin_connect: "#16A34A",
  sms: "#8E8E94",
  in_person: "#92400E",
  meeting_set: "#DC2626",
};

function emptyCounts(): ChannelCounts {
  return {
    phone_dial: 0,
    phone_connect: 0,
    email_send: 0,
    email_reply: 0,
    linkedin_msg: 0,
    linkedin_connect: 0,
    sms: 0,
    in_person: 0,
    meeting_set: 0,
  };
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function storageKey(userId: string, date: string): string {
  return `aip:bdr-channel:${userId}:${date}`;
}

export function getChannelCounts(
  userId: string,
  date: string = todayStr(),
): ChannelCounts {
  try {
    const raw = localStorage.getItem(storageKey(userId, date));
    if (!raw) return emptyCounts();
    const parsed = JSON.parse(raw) as Partial<ChannelCounts>;
    const merged = emptyCounts();
    for (const k of CHANNEL_KEYS) {
      merged[k] = typeof parsed[k] === "number" ? (parsed[k] as number) : 0;
    }
    return merged;
  } catch {
    return emptyCounts();
  }
}

export function incrementChannel(
  userId: string,
  channel: BdrChannel,
  date: string = todayStr(),
): ChannelCounts {
  const current = getChannelCounts(userId, date);
  current[channel] = (current[channel] || 0) + 1;
  try {
    localStorage.setItem(storageKey(userId, date), JSON.stringify(current));
    window.dispatchEvent(
      new CustomEvent("aip:bdr-channel-logged", {
        detail: { userId, channel, date, counts: current },
      }),
    );
  } catch {
    // ignore quota errors — UI still updates from the returned object
  }
  return current;
}

export function totalConversations(c: ChannelCounts): number {
  // "Conversation" = a two-way exchange. Phone connect, email reply,
  // LinkedIn accept, SMS, in-person all count. Outbound-only events
  // (dial, send, message, meeting_set) do not.
  return (
    c.phone_connect +
    c.email_reply +
    c.linkedin_connect +
    c.sms +
    c.in_person
  );
}

export function totalAcrossChannels(c: ChannelCounts): number {
  return CHANNEL_KEYS.reduce((sum, k) => sum + (c[k] || 0), 0);
}
